
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { BULK_EDIT_PAGE_SIZE } from '@/utils/constants';
import { countActiveFilters } from '@/utils/bulkJob';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import PaginationControls from '@/components/shared/suites/PaginationControls.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import TagPreview from '@/components/shared/TagPreview.vue';
import SelectLocationsModal from '@/components/multi-location/SelectLocationsModal.vue';
import BulkEditProgress from '@/components/multi-location/BulkEditProgress.vue';
import ActionHistoryModal from '@/components/multi-location/ActionHistoryModal.vue';
import BulkActionModal from '@/components/multi-location/modals/BulkActionModal.vue';
import BulkFiltersModal from '@/components/multi-location/modals/BulkFiltersModal.vue';

const namespace: string = 'bulkEdit';
const jobNamespace: string = 'bulkItemEdit';
const BULK_ACTIONS: BulkEditAction[] = ['featured', 'upsell', 'tags', 'allergens', 'diets'];

@Component<BulkEditMenu>({
	components: {
		TableHeaders,
		PaginationControls,
		TextButton,
		TextField,
		TagPreview,
		SelectLocationsModal,
		BulkEditProgress,
		ActionHistoryModal,
		BulkActionModal,
		BulkFiltersModal
	}
})
export default class BulkEditMenu extends Vue {
	@Action('fetchMenuRows', { namespace }) private fetchMenuRows!: (params: BulkMenuQueryParams) => Promise<void>;
	@Action('fetchLocationTags', { namespace }) private fetchLocationTags!: (locationIds: string[]) => Promise<MenuItemTag[]>;
	@Action('setLocationIds', { namespace }) private setSelectedLocationIds!: (locationIds: string[]) => void;
	@Action('startJob', { namespace: jobNamespace }) private startJob!: (payload: BulkItemEditJobPayload) => Promise<void>;
	@Action('fetchActiveJob', { namespace: jobNamespace }) private fetchActiveJob!: () => Promise<void>;
	@Action('fetchViewedJob', { namespace: jobNamespace }) private fetchViewedJob!: (jobId: string) => Promise<void>;
	@Action('fetchJobHistory', { namespace: jobNamespace }) private fetchJobHistory!: (params?: TableFilterParams) => Promise<void>;
	@Action('cancelJob', { namespace: jobNamespace }) private cancelJob!: (jobId: string) => Promise<void>;
	@Action('stopPolling', { namespace: jobNamespace }) private stopPolling!: () => void;
	@Action('clearViewedJob', { namespace: jobNamespace }) private clearViewedJob!: () => void;
	@Getter('getMenuRows', { namespace }) private menuRows!: BulkMenuItemRow[];
	@Getter('getCount', { namespace }) private count!: number;
	@Getter('getSelectedLocationIds', { namespace }) private selectedLocationIds!: string[];
	@Getter('getRestaurants', { namespace: 'auth' }) private restaurants!: Tenant[] | null;
	@Getter('getActiveJob', { namespace: jobNamespace }) private activeJob!: BulkJob | null;
	@Getter('getViewedJob', { namespace: jobNamespace }) private viewedJob!: BulkJob | null;
	@Getter('hasActiveJob', { namespace: jobNamespace }) private isJobRunning!: boolean;
	@Getter('getJobHistory', { namespace: jobNamespace }) private jobHistory!: BulkJob[];
	@Getter('getJobHistoryCount', { namespace: jobNamespace }) private jobHistoryCount!: number;
	@Getter('getJobHistoryFilters', { namespace: jobNamespace }) private historyFilters!: TableFilterParams;

	@Watch('activeJob')
	private onJobFinished(job: BulkJob | null, previousJob: BulkJob | null): void {
		if (!previousJob || job) {
			return;
		}
		this.loadRows();
	}

	private readonly bulkActions: BulkEditAction[] = BULK_ACTIONS;
	private readonly itemsPerPage: number = BULK_EDIT_PAGE_SIZE;
	private currentPage: number = 1;
	private selectionCount: number = 0;
	private searchInput: string = '';
	private sortBy: string = 'name';
	private sortDirection: 'asc' | 'desc' = 'asc';
	private locationIds: string[] = [];
	private locationTags: MenuItemTag[] = [];
	private selectedItems: BulkEditItemRef[] = [];
	private excludedItems: BulkEditItemRef[] = [];
	private menuFilters: BulkMenuFilters = {};
	private selectionQuery: BulkEditQuerySelection | null = null;
	private selectedAction: BulkEditAction | null = null;
	private isFetching: boolean = true;
	private isLoadingHistory: boolean = false;
	private showLocationsModal: boolean = false;
	private showFiltersModal: boolean = false;
	private showHistoryModal: boolean = false;

	private get displayedJob(): BulkJob | null {
		return this.activeJob ?? this.viewedJob;
	}

	private get hasMultipleLocations(): boolean {
		return (this.restaurants?.length ?? 0) > 1;
	}

	private get totalPages(): number {
		return Math.max(1, Math.ceil(this.count / this.itemsPerPage));
	}

	private get totalHistoryPages(): number {
		const limit: number = this.historyFilters.limit || 10;
		return Math.max(1, Math.ceil(this.jobHistoryCount / limit));
	}

	private get activeFilterCount(): number {
		return countActiveFilters(this.menuFilters);
	}

	/**
	 * SKUs come from the POS, so the column and its filter only show when the selection holds at least
	 * one integrated location.
	 *
	 * @return {boolean}
	 */
	private get hasPosIntegratedLocation(): boolean {
		const selected: Set<string> = new Set(this.locationIds);
		return (this.restaurants ?? []).some((location: Tenant) => selected.has(location.id) && location.pos_integrated);
	}

	/**
	 * Event packages are a Bowlero concept, so the filter only shows when a Bowlero location is in the
	 * selection.
	 *
	 * @return {boolean}
	 */
	private get hasBowleroLocation(): boolean {
		const selected: Set<string> = new Set(this.locationIds);
		return (this.restaurants ?? []).some((location: Tenant) => selected.has(location.id)
			&& (location.group === 'bowlero' || !!location.groups?.includes('bowlero')));
	}

	private get filtersButtonText(): string {
		const count: number = this.activeFilterCount;
		return this.$t('multi_location_settings.bulk_edit_menu.filters_btn') + (count ? ` (${count})` : '');
	}

	/**
	 * The search / sort / filter half of the query. Shared by the paged fetch and by the query
	 * selection, so the two can never disagree on what "matching" means.
	 *
	 * @return {BulkMenuQueryParams}
	 */
	private get menuQuery(): BulkMenuQueryParams {
		return {
			location_ids: this.locationIds,
			search: this.searchInput || undefined,
			sortBy: this.sortBy,
			sortDirection: this.sortDirection,
			filters: this.activeFilterCount ? this.menuFilters : undefined
		};
	}

	private get tableHeaders(): TableHeader[] {
		const selectedCount: number = this.selectedCount;
		return [
			{ text: '', name: 'select-all', class: 'checkbox-col' },
			{
				text: this.$t('multi_location_settings.bulk_edit_menu.table.item') + (selectedCount ? ` (${selectedCount})` : ''),
				name: 'name',
				sort: this.sortDirection
			},
			...(this.hasPosIntegratedLocation
				? [{ text: this.$t('multi_location_settings.bulk_edit_menu.table.sku'), name: 'sku', sort: this.sortDirection }]
				: []),
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.menu'), name: 'menu_name', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.section'), name: 'section_name', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.location'), name: 'location_name', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.upsell'), name: 'upsell_item', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.featured'), name: 'featured_item', sort: this.sortDirection },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.tags') },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.diets') },
			{ text: this.$t('multi_location_settings.bulk_edit_menu.table.allergens') }
		];
	}

	/**
	 * Item ids repeat across locations and across the two item shapes, so selection is tracked by
	 * (item_type, item, location) — never by id alone.
	 *
	 * @return {Set<string>}
	 */
	private get selectedKeys(): Set<string> {
		return new Set(this.selectedItems.map(item => this.rowKey(item)));
	}

	private get excludedKeys(): Set<string> {
		return new Set(this.excludedItems.map(item => this.rowKey(item)));
	}

	private get isQuerySelection(): boolean {
		return !!this.selectionQuery;
	}

	/**
	 * How many items the next action would hit: every match minus the un-ticked ones in query mode,
	 * or just the ticked rows otherwise.
	 *
	 * @return {number}
	 */
	private get selectedCount(): number {
		if (this.isQuerySelection) {
			return Math.max(this.selectionCount - this.excludedItems.length, 0);
		}
		return this.selectedItems.length;
	}

	private get currentPageSelectedCount(): number {
		return this.menuRows.filter(row => this.isItemSelected(row)).length;
	}

	private get isCurrentPageFullySelected(): boolean {
		return !!this.menuRows.length && this.currentPageSelectedCount === this.menuRows.length;
	}

	private get isCurrentPagePartiallySelected(): boolean {
		return !!this.currentPageSelectedCount && this.currentPageSelectedCount < this.menuRows.length;
	}

	/**
	 * Offer the wider scope once the page is fully ticked and there is something left to tick on the
	 * other pages; keep the banner up afterwards so the query selection can be cleared.
	 *
	 * @return {boolean}
	 */
	private get showSelectionBanner(): boolean {
		return this.isQuerySelection || (this.isCurrentPageFullySelected && this.count > this.menuRows.length);
	}

	/**
	 * What the selection banner states: the whole query (minus anything un-ticked) or just this page.
	 *
	 * @return {string}
	 */
	private get selectionText(): string {
		if (!this.isQuerySelection) {
			return this.$tc('multi_location_settings.bulk_edit_menu.selection.page_selected', this.menuRows.length, { count: this.menuRows.length });
		}
		return this.excludedItems.length
			? this.$tc('multi_location_settings.bulk_edit_menu.selection.all_selected_minus', this.selectedCount, { count: this.selectedCount })
			: this.$tc('multi_location_settings.bulk_edit_menu.selection.all_selected', this.selectedCount, { count: this.selectedCount });
	}

	/**
	 * Forces the header checkbox to re-mount so it can't get stuck showing a stale state.
	 *
	 * @return {'unchecked' | 'indeterminate' | 'checked'}
	 */
	private get headerCheckboxState(): 'unchecked' | 'indeterminate' | 'checked' {
		if (this.isCurrentPageFullySelected) {
			return 'checked';
		}
		if (this.isCurrentPagePartiallySelected) {
			return 'indeterminate';
		}
		return 'unchecked';
	}

	private async created(): Promise<void> {
		/*
		 * The selected locations live in the store, mirrored to sessionStorage, so a reload keeps
		 * working — and a job started before the reload can still be picked back up below.
		 */
		this.locationIds = this.selectedLocationIds;

		if (!this.locationIds.length) {
			this.$router.replace('/multi-location-settings').catch(() => {});
			return;
		}

		await this.loadLocationTags();
		await this.loadRows();

		try {
			await this.fetchActiveJob();
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	private beforeDestroy(): void {
		cancelDebounce(this.debouncedSearch);
		this.stopPolling();
		this.clearViewedJob();
	}

	/**
	 * Fetch a page of menu items for the selected locations.
	 *
	 * @return {Promise<void>}
	 */
	private async loadRows(): Promise<void> {
		this.isFetching = true;
		try {
			await this.fetchMenuRows({
				...this.menuQuery,
				page: this.currentPage,
				limit: this.itemsPerPage
			});
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.bulk_edit_menu.error_fetching', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isFetching = false;
		}
	}

	/**
	 * Load the tags defined across the selected locations, deduped by name. This is a union, not an
	 * intersection — a tag here need not exist in every selected location.
	 *
	 * @return {Promise<void>}
	 */
	private async loadLocationTags(): Promise<void> {
		try {
			this.locationTags = await this.fetchLocationTags(this.locationIds);
		}
		catch (error) {
			this.locationTags = [];
			Toast.error(this.$t('multi_location_settings.bulk_edit_menu.error_fetching_tags', { errorMessage: toErrorMessage(error) }));
		}
	}

	private goToPreviousPage(): void {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.loadRows();
		}
	}

	private goToNextPage(): void {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.loadRows();
		}
	}

	/**
	 * Sort by a column, flipping the direction when the same column is clicked again.
	 *
	 * @param {string} columnName
	 * @return {void}
	 */
	private onToggleSort(columnName: string): void {
		if (!columnName || columnName === 'select-all') {
			return;
		}
		if (this.sortBy === columnName) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			this.sortBy = columnName;
			this.sortDirection = 'asc';
		}
		this.currentPage = 1;
		this.loadRows();
	}

	@Debounce(500)
	private debouncedSearch(): void {
		this.dropQuerySelection();
		this.currentPage = 1;
		this.loadRows();
	}

	private clearSearch(): void {
		this.searchInput = '';
		this.dropQuerySelection();
		this.currentPage = 1;
		this.loadRows();
	}

	/**
	 * Apply the filters from the modal and reload from page 1.
	 *
	 * @param {BulkMenuFilters} filters
	 * @return {void}
	 */
	private onApplyFilters(filters: BulkMenuFilters): void {
		this.showFiltersModal = false;
		this.menuFilters = filters;
		this.clearSelection();
		this.currentPage = 1;
		this.loadRows();
	}

	/**
	 * Changing the selected locations changes the whole page context, so persist the new set and reload
	 * the table, its common tags and the selection from scratch.
	 *
	 * @param {string[]} locationIds
	 * @return {void}
	 */
	private onLocationsChanged(locationIds: string[]): void {
		this.showLocationsModal = false;
		this.locationIds = locationIds;
		this.clearSelection();
		this.currentPage = 1;
		this.setSelectedLocationIds(locationIds);
		this.loadLocationTags();
		this.loadRows();
	}

	/**
	 * Start the bulk edit as a background job and close the modal straight away. Progress is rendered
	 * by the card above the table, not by the modal.
	 *
	 * @param {Pick<BulkItemEditJobPayload, 'action' | 'values' | 'assign'>} payload - the action half from the modal
	 * @return {Promise<void>}
	 */
	private async startItemEditJob(payload: Pick<BulkItemEditJobPayload, 'action' | 'values' | 'assign'>): Promise<void> {
		this.selectedAction = null;

		try {
			/*
			 * A query selection can stand for thousands of rows, so it travels as the query the table was
			 * showing plus whatever was un-ticked, and the backend resolves it.
			 */
			if (this.selectionQuery) {
				await this.startJob({
					...payload,
					query: { ...this.selectionQuery },
					exclude: [...this.excludedItems]
				});
			}
			else {
				/*
				 * Otherwise send the exact (item, location) pairs that were ticked. Sending bare item ids
				 * plus the location set would be a cross product — it would hit item #42 in every selected
				 * location, not just the rows the operator chose, and item ids aren't unique across
				 * locations anyway.
				 */
				await this.startJob({
					...payload,
					items: [...this.selectedItems]
				});
			}

			/*
			 * The selection has been handed to the job, so it is spent whether or not the job succeeds.
			 */
			this.clearSelection();
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	/**
	 * @param {string} jobId
	 * @return {Promise<void>}
	 */
	private async onCancelJob(jobId: string): Promise<void> {
		try {
			await this.cancelJob(jobId);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	/**
	 * Open the action history on a freshly loaded first page. The modal's search box mounts empty
	 * every time, so the stored search is dropped with it.
	 *
	 * @return {Promise<void>}
	 */
	private async openHistory(): Promise<void> {
		this.showHistoryModal = true;
		await this.applyHistoryFilters({ ...this.historyFilters, page: 1, search: undefined });
	}

	/**
	 * @param {TableFilterParams} next
	 * @return {Promise<void>}
	 */
	private async applyHistoryFilters(next: TableFilterParams): Promise<void> {
		this.isLoadingHistory = true;
		try {
			await this.fetchJobHistory(next);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isLoadingHistory = false;
		}
	}

	private async goToPreviousHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current > 1) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current - 1 });
		}
	}

	private async goToNextHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current < this.totalHistoryPages) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current + 1 });
		}
	}

	/**
	 * @param {string} columnName
	 * @return {Promise<void>}
	 */
	private async onHistorySortChange(columnName: string): Promise<void> {
		const nextDirection: 'asc' | 'desc' = this.historyFilters.sortBy === columnName
			? (this.historyFilters.sortDirection === 'asc' ? 'desc' : 'asc')
			: 'asc';
		await this.applyHistoryFilters({
			...this.historyFilters,
			page: 1,
			sortBy: columnName,
			sortDirection: nextDirection
		});
	}

	/**
	 * Search past actions by name. Debounced by the history component.
	 *
	 * @param {string} search
	 * @return {Promise<void>}
	 */
	private async onHistorySearch(search: string): Promise<void> {
		await this.applyHistoryFilters({ ...this.historyFilters, page: 1, search: search || undefined });
	}

	/**
	 * Open a historical job in the progress card. The history rows carry counts only — the list
	 * endpoint deliberately omits per-item errors so a page of jobs doesn't haul every failure
	 * payload — so seeing which items failed means loading the job's detail and handing the view
	 * back to the card, which already renders that breakdown. Mirrors how the enterprise menu-pull
	 * and menu-replication histories behave.
	 *
	 * @param {string} jobId
	 * @return {Promise<void>}
	 */
	private async viewHistoryJob(jobId: string): Promise<void> {
		try {
			await this.fetchViewedJob(jobId);
			// Only close once there is something to show — otherwise the modal would vanish and
			// reveal nothing behind it.
			if (this.viewedJob) {
				this.showHistoryModal = false;
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	/**
	 * Identity of a table row. A row is one menu item IN one location, and item ids repeat across
	 * locations, and the same id can exist as both a section_item and a menu_item in one location —
	 * so the key needs item_type and location_id, not the id alone.
	 *
	 * @param {BulkEditItemRef} row
	 * @return {string}
	 */
	private rowKey(row: BulkEditItemRef): string {
		return `${row.location_id}:${row.item_type}:${row.id}`;
	}

	/**
	 * @param {BulkMenuItemRow} row
	 * @return {boolean}
	 */
	private isItemSelected(row: BulkMenuItemRow): boolean {
		const key: string = this.rowKey(row);

		/*
		 * Under a query selection everything is in unless it was explicitly taken out.
		 */
		if (this.isQuerySelection) {
			return !this.excludedKeys.has(key);
		}
		return this.selectedKeys.has(key);
	}

	/**
	 * Add or remove a row from the selection, keyed on the (item, location) pair. Under a query
	 * selection this edits the exclusion list instead — the row is still matched by the query, it is
	 * just carved out of it.
	 *
	 * @param {BulkMenuItemRow} row
	 * @return {void}
	 */
	private toggleItemSelection(row: BulkMenuItemRow): void {
		const key: string = this.rowKey(row);

		if (this.isQuerySelection) {
			if (this.excludedKeys.has(key)) {
				this.excludedItems = this.excludedItems.filter(item => this.rowKey(item) !== key);
				return;
			}
			this.excludedItems.push({ id: row.id, item_type: row.item_type, location_id: row.location_id });
			return;
		}

		if (this.selectedKeys.has(key)) {
			this.selectedItems = this.selectedItems.filter(item => this.rowKey(item) !== key);
			return;
		}

		this.selectedItems.push({ id: row.id, item_type: row.item_type, location_id: row.location_id });
	}

	/**
	 * Select every row on the current page, or clear the page's rows when any of them are selected.
	 * Selections on other pages are left alone.
	 *
	 * @return {void}
	 */
	private toggleCurrentPageSelection(): void {
		const currentPageKeys: Set<string> = new Set(this.menuRows.map(row => this.rowKey(row)));

		if (this.isQuerySelection) {
			const pageRefs: BulkEditItemRef[] = this.menuRows.map(row => ({ id: row.id, item_type: row.item_type, location_id: row.location_id }));

			if (this.currentPageSelectedCount > 0) {
				const stillExcluded: BulkEditItemRef[] = this.excludedItems.filter(item => !currentPageKeys.has(this.rowKey(item)));
				this.excludedItems = [...stillExcluded, ...pageRefs];
				return;
			}

			this.excludedItems = this.excludedItems.filter(item => !currentPageKeys.has(this.rowKey(item)));
			return;
		}

		if (this.currentPageSelectedCount > 0) {
			this.selectedItems = this.selectedItems.filter(item => !currentPageKeys.has(this.rowKey(item)));
			return;
		}

		const newItems: BulkEditItemRef[] = this.menuRows.map(row => ({ id: row.id, item_type: row.item_type, location_id: row.location_id }));
		this.selectedItems = [...this.selectedItems, ...newItems];
	}

	/**
	 * Switch the selection from "these rows" to "everything the table is showing". Nothing is fetched:
	 * the query itself becomes the selection, so the count is irrelevant to the payload size.
	 *
	 * @return {void}
	 */
	private selectAllMatchingItems(): void {
		const { location_ids, search, filters }: BulkMenuQueryParams = this.menuQuery;
		this.selectionQuery = { location_ids, search, filters };
		this.selectionCount = this.count;
		this.selectedItems = [];
		this.excludedItems = [];
	}

	private clearSelection(): void {
		this.selectedItems = [];
		this.dropQuerySelection();
	}

	/**
	 * Drop a query selection when the query behind it changes, since it would no longer stand for what
	 * the operator was shown. Row-by-row selections are left alone.
	 *
	 * @return {void}
	 */
	private dropQuerySelection(): void {
		this.selectionQuery = null;
		this.selectionCount = 0;
		this.excludedItems = [];
	}
}
